import { motion } from 'framer-motion';
import { useEffect, useRef } from 'react';
import { 
  ShoppingBasket, 
  Pill, 
  Shirt, 
  Apple, 
  Baby, 
  Home, 
  Utensils, 
  Gift,
  Sparkles,
  Coffee,
  Book,
  Car
} from 'lucide-react';

const ProductIcons = () => {
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const scrollContainer = scrollRef.current;
    if (!scrollContainer) return;

    let scrollSpeed = 0.25;
    let animationId: number;

    const scroll = () => {
      if (scrollContainer) {
        scrollContainer.scrollLeft += scrollSpeed;
        
        // Reset to beginning when reaching the end for infinite scroll
        if (scrollContainer.scrollLeft >= scrollContainer.scrollWidth / 3) {
          scrollContainer.scrollLeft = 0;
        }
      }
      animationId = requestAnimationFrame(scroll);
    };

    // Start auto-scroll
    animationId = requestAnimationFrame(scroll);

    // Pause on hover
    const handleMouseEnter = () => {
      cancelAnimationFrame(animationId);
    };

    const handleMouseLeave = () => {
      animationId = requestAnimationFrame(scroll);
    };

    scrollContainer.addEventListener('mouseenter', handleMouseEnter);
    scrollContainer.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      cancelAnimationFrame(animationId);
      if (scrollContainer) {
        scrollContainer.removeEventListener('mouseenter', handleMouseEnter);
        scrollContainer.removeEventListener('mouseleave', handleMouseLeave);
      }
    };
  }, []);

  const categories = [
    { icon: ShoppingBasket, name: "Groceries", color: "text-green-500" },
    { icon: Pill, name: "Health", color: "text-blue-500" },
    { icon: Shirt, name: "Fashion", color: "text-purple-500" },
    { icon: Apple, name: "Fresh Food", color: "text-red-500" },
    { icon: Baby, name: "Baby Care", color: "text-pink-500" },
    { icon: Home, name: "Home", color: "text-indigo-500" },
    { icon: Utensils, name: "Kitchen", color: "text-orange-500" },
    { icon: Gift, name: "Gifts", color: "text-yellow-500" },
    { icon: Sparkles, name: "Beauty", color: "text-rose-500" },
    { icon: Coffee, name: "Beverages", color: "text-amber-500" },
  ];

  return (
    <section className="py-16 bg-white relative overflow-hidden">
      <style>{`
        .hide-scrollbar::-webkit-scrollbar {
          display: none;
        }
      `}</style>
      {/* Background pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-0 left-1/4 w-64 h-64 bg-red-500 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-1/4 w-64 h-64 bg-orange-500 rounded-full blur-3xl"></div>
      </div>
      
      <div className="container mx-auto px-4 relative z-10">
        <motion.div 
          className="text-center mb-12"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <h2 className="text-3xl font-bold text-gray-800 mb-4">
            Shop Across <span className="bg-gradient-to-r from-red-500 to-orange-500 bg-clip-text text-transparent">Categories</span>
          </h2>
          {/* <p className="text-gray-600 max-w-2xl mx-auto">
            From daily essentials to luxury items, find everything you need across multiple product categories
          </p> */}
        </motion.div>

        {/* Responsive layout: vertical on mobile, horizontal on desktop */}
        <div className="flex flex-col lg:flex-row lg:items-center gap-8">
          {/* Stats section - appears first on mobile, second on desktop */}
          <motion.div 
            className="order-2 lg:order-2 flex-shrink-0 lg:ml-auto"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.5 }}
            viewport={{ once: true }}
          >
            <div className="flex items-center justify-center space-x-4 sm:space-x-6 bg-gradient-to-r from-red-50 to-orange-50 rounded-2xl px-4 sm:px-6 py-3 border border-red-100 mx-auto lg:mx-0 max-w-fit">
              <div className="text-center">
                <div className="text-lg sm:text-xl font-bold text-red-500">100K+</div>
                <div className="text-xs text-gray-600">Products</div>
              </div>
              <div className="w-px h-6 bg-gray-300"></div>
              <div className="text-center">
                <div className="text-lg sm:text-xl font-bold text-orange-500">15K+</div>
                <div className="text-xs text-gray-600">Brands</div>
              </div>
              <div className="w-px h-6 bg-gray-300"></div>
              <div className="text-center">
                <div className="text-lg sm:text-xl font-bold text-red-600">50+</div>
                <div className="text-xs text-gray-600">Categories</div>
              </div>
            </div>
          </motion.div>

          {/* Horizontally scrollable categories */}
          <div className="order-1 lg:order-1 flex-1 overflow-hidden">
            <div 
              ref={scrollRef}
              className="flex items-center gap-4 sm:gap-6 overflow-x-auto pb-4 hide-scrollbar" 
              style={{ 
                scrollbarWidth: 'none', 
                msOverflowStyle: 'none'
              }}
            >
               {[...categories, ...categories, ...categories, ...categories].map((category, index) => (
                 <motion.div
                   key={index}
                   className="flex flex-col items-center group cursor-pointer flex-shrink-0"
                   initial={{ opacity: 0, scale: 0.8 }}
                   whileInView={{ opacity: 1, scale: 1 }}
                   transition={{ duration: 0.6, delay: (index % categories.length) * 0.05 }}
                   viewport={{ once: true }}
                   whileHover={{ scale: 1.05 }}
                 >
                   <div className={`w-12 h-12 sm:w-16 sm:h-16 rounded-full bg-gray-50 border-2 border-gray-100 flex items-center justify-center mb-2 transition-all duration-300 group-hover:bg-gradient-to-br group-hover:from-red-50 group-hover:to-orange-50 group-hover:border-red-200 group-hover:shadow-lg`}>
                     <category.icon 
                       size={20} 
                       className={`sm:w-[26px] sm:h-[26px] ${category.color} transition-all duration-300 group-hover:scale-110`} 
                     />
                   </div>
                   <span className="text-xs font-medium text-gray-600 group-hover:text-gray-800 transition-colors text-center whitespace-nowrap">
                     {category.name}
                   </span>
                 </motion.div>
               ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProductIcons; 